import axios from '@/_utils/axios';
import React, { useEffect, useRef, useState } from 'react';
import BulkSearchJob from './BulkSearchJob';
import { Order, OrderJob } from '@/redux/slices/slice_types';
import { Grid, Pagination, CircularProgress } from '@mui/material';
import { useAppDispatch } from '@/redux/hooks';

export enum BulkTabStatus {
  IN_PROGRESS = 'in_progress',
  SUBMITTING = 'submitting',
  CANCELLED = 'cancelled',
  IN_ERROR = 'in_error',
  FAILED = 'failed'
}

const FILTER_BY_STATUS = {
  [BulkTabStatus.IN_PROGRESS]: 'in_progress',
  [BulkTabStatus.SUBMITTING]: 'submitting',
  [BulkTabStatus.CANCELLED]: 'cancelled',
  [BulkTabStatus.IN_ERROR]: 'in_error',
  [BulkTabStatus.FAILED]: 'failed'
};

interface BulkTabContentProps {
  orderId: number;
  type: BulkTabStatus[];
  active: boolean;
  jobStats: Order['job_stats'];
}

const PAGE_SIZE = 20;
const DEBOUNCE_DELAY = 3000; // 3 seconds

const BulkTabContent = ({ orderId, type, active, jobStats }: BulkTabContentProps) => {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ page: 0, total: 0 });
  const [loading, setIsLoading] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (active) {
      fetchData();
    }
  }, [active]);

  useEffect(() => {
    active && fetchData();
  }, [pagination.page]);

  useEffect(() => {
    // get the count of the relevant job stats for this tab based on type
    const count = type.reduce((acc, t) => acc + (jobStats[t] || 0), 0);
    if (active) {
      setPagination({ ...pagination, total: count });
    }
    if (active && count <= 20) {
      debounceFetchData();
    }
  }, [jobStats]);

  const debounceFetchData = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      fetchData();
    }, DEBOUNCE_DELAY);
  };

  const fetchData = () => {
    setIsLoading(true);
    axios
      .post(`/core/jobs/search/?page_num=${pagination.page}&page_size=${PAGE_SIZE}`, {
        order_hid_filter: ['W' + orderId],
        status_filter: type.map((t) => FILTER_BY_STATUS[t])
      })
      .then((response) => {
        setData(response.data.data);
        setPagination(response.data.pagination);
      })
      .catch((error) => console.error(error))
      .finally(() => setIsLoading(false));
  };

  const onPageChange = (_e: React.ChangeEvent<unknown>, page: number) => {
    setPagination({ ...pagination, page: page - 1 });
  };

  return (
    <div style={{ opacity: loading ? 0.5 : 1 }}>
      {loading && !data?.length ? 'Loading...' : data.map((job: OrderJob) => <BulkSearchJob orderId={orderId} key={job.id} job={job} type />)}
      <Grid container justifyContent="center" sx={{ mt: 5 }}>
        {!loading && pagination.total > PAGE_SIZE && (
          <Pagination
            count={Math.ceil(pagination.total / PAGE_SIZE)}
            page={pagination.page + 1}
            onChange={onPageChange}
            color="primary"
            size="small"
          />
        )}
      </Grid>
    </div>
  );
};

export default BulkTabContent;
